/**
 * Amsterdamse tijd voor het live verslag.
 *
 * `dateFormats.ts` laat de tijdzone bewust weg: bestaande schermen tonen de kloktijd van het
 * toestel. Het live verslag doet dat niet. Een tijdlijn die ouders thuis volgen hoort voor
 * iedereen dezelfde tijd te tonen, ook op een telefoon die in een andere tijdzone staat, dus
 * hier staat de tijdzone er altijd expliciet bij.
 *
 * Een moment gaat over de lijn als tekst met Amsterdamse offset, zodat de kloktijd leesbaar
 * blijft en het moment eenduidig is. Rekenen gebeurt altijd op epoch-milliseconden, dus ook
 * over de omschakeling naar zomer- of wintertijd heen klopt een duur.
 */

const TIJDZONE = 'Europe/Amsterdam';

const onderdelenFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: TIJDZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  // h23 in plaats van hour12: false, anders geeft ICU middernacht als uur 24.
  hourCycle: 'h23',
});

const onderdelenOp = (epochMs: number) => {
  const onderdelen = onderdelenFormatter.formatToParts(epochMs);
  const waarde = (type: Intl.DateTimeFormatPartTypes): number =>
    parseInt(onderdelen.find((onderdeel) => onderdeel.type === type)?.value ?? '0', 10);

  return {
    jaar: waarde('year'),
    maand: waarde('month'),
    dag: waarde('day'),
    uur: waarde('hour'),
    minuut: waarde('minute'),
    seconde: waarde('second'),
  };
};

const opLengte = (getal: number, lengte = 2): string => String(getal).padStart(lengte, '0');

/** "2026-09-12T14:03:12.345+02:00" voor dit moment. */
export const toAmsterdamIso = (moment: number): string => {
  // Hele milliseconden: een moment met een breuk (bijvoorbeeld na een klokcorrectie die op
  // een halve milliseconde uitkomt) gaf anders ".123.5" en daarmee een onleesbare tekst.
  const epochMs = Math.floor(moment);
  const k = onderdelenOp(epochMs);
  const heleSeconden = Math.floor(epochMs / 1000) * 1000;
  const offset = Math.round(
    (Date.UTC(k.jaar, k.maand - 1, k.dag, k.uur, k.minuut, k.seconde) - heleSeconden) / 60000,
  );
  const teken = offset < 0 ? '-' : '+';
  const absoluut = Math.abs(offset);

  return (
    `${k.jaar}-${opLengte(k.maand)}-${opLengte(k.dag)}` +
    `T${opLengte(k.uur)}:${opLengte(k.minuut)}:${opLengte(k.seconde)}` +
    `.${opLengte(epochMs - heleSeconden, 3)}` +
    `${teken}${opLengte(Math.floor(absoluut / 60))}:${opLengte(absoluut % 60)}`
  );
};

const tijdFormatters = new Map<string, Intl.DateTimeFormat>();

/** "14:03" in Amsterdamse tijd, in de opmaak van de taal. */
export const formatAmsterdamTime = (epochMs: number, intlLocale: string): string => {
  let formatter = tijdFormatters.get(intlLocale);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(intlLocale, {
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
      timeZone: TIJDZONE,
    });
    tijdFormatters.set(intlLocale, formatter);
  }
  return formatter.format(epochMs);
};

/** De kalenderdag in Amsterdam: "2026-09-12". */
export const amsterdamDateKey = (epochMs: number): string => {
  const k = onderdelenOp(epochMs);
  return `${k.jaar}-${opLengte(k.maand)}-${opLengte(k.dag)}`;
};

const HEEFT_TIJDZONE = /(Z|[+-]\d{2}:\d{2})$/;

/**
 * Valt de wedstrijd vandaag, in Amsterdam?
 *
 * De API geeft `startTime` als Amsterdamse kloktijd zonder tijdzone. `new Date()` zou die als
 * lokale tijd lezen - of in sommige omgevingen als UTC - en er een uur of twee naast zitten,
 * genoeg om rond middernacht de verkeerde dag te kiezen. De datum staat letterlijk vooraan in
 * de tekst, dus die nemen we zo over.
 */
export const isMatchDayInAmsterdam = (startTime: string | null | undefined, nowMs: number): boolean => {
  if (!startTime) return false;

  const wedstrijddag = HEEFT_TIJDZONE.test(startTime)
    ? amsterdamDateKey(Date.parse(startTime))
    : startTime.slice(0, 10);

  return wedstrijddag === amsterdamDateKey(nowMs);
};

/** Zoveel uur voor de aftrap gaat het live verslag open voor de coach. */
export const LIVE_VOORLOOP_UREN = 36;

const KLOKTIJD = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/;

/**
 * Het moment van een Amsterdamse kloktijd zonder tijdzone. Eerst als UTC gelezen en dan de
 * Amsterdamse offset van dat moment eraf; twee rondes, zodat het ook rond de omschakeling naar
 * zomer- of wintertijd op de goede offset uitkomt.
 */
const amsterdamseKloktijdNaarEpoch = (tekst: string): number => {
  const delen = KLOKTIJD.exec(tekst);
  if (!delen) return NaN;

  const [, jaar, maand, dag, uur = '0', minuut = '0', seconde = '0'] = delen;
  const alsUtc = Date.UTC(+jaar, +maand - 1, +dag, +uur, +minuut, +seconde);

  let moment = alsUtc;
  for (let ronde = 0; ronde < 2; ronde++) {
    const k = onderdelenOp(moment);
    const offset = Date.UTC(k.jaar, k.maand - 1, k.dag, k.uur, k.minuut, k.seconde) - moment;
    moment = alsUtc - offset;
  }
  return moment;
};

/**
 * Staat het live verslag open voor de coach? Vanaf `LIVE_VOORLOOP_UREN` voor de aftrap, zodat
 * het de dag ervoor al opvalt, bijvoorbeeld bij het maken van de opstelling, en daarna de hele
 * wedstrijddag.
 */
export const isLiveWindowOpen = (startTime: string | null | undefined, nowMs: number): boolean => {
  if (!startTime) return false;
  if (isMatchDayInAmsterdam(startTime, nowMs)) return true;

  const aftrap = HEEFT_TIJDZONE.test(startTime) ? Date.parse(startTime) : amsterdamseKloktijdNaarEpoch(startTime);
  if (Number.isNaN(aftrap)) return false;

  return nowMs < aftrap && aftrap - nowMs <= LIVE_VOORLOOP_UREN * 60 * 60 * 1000;
};
